<template>
  <section data-testid="lead-history">
    <div class="mb-4 flex items-center justify-between gap-4">
      <h3 class="txt-headline-m">{{ t('LeadDetail.history.title') }}</h3>
      <div class="flex items-center gap-2">
        <button type="button" class="btn-primary-color-xs whitespace-nowrap" data-testid="lead-note-add" @click="noteDialog?.open()">
          <PencilIcon class="lucide-icon-s" /> {{ t('LeadDetail.notes.addAction') }}
        </button>
        <LeadStatusSelect
          :model-value="status"
          :disabled="statusLoading"
          @update:model-value="(value: LeadStatusEnum) => emit('update:status', value)"
        />
      </div>
    </div>

    <p v-if="entries.length === 0" class="txt-body-m text-subtlest" data-testid="lead-history-empty">
      {{ t('LeadDetail.history.empty') }}
    </p>
    <template v-else>
      <ul class="flex flex-col gap-4" data-testid="lead-history-list">
        <li
          v-for="entry in visibleEntries"
          :key="entry.id"
          :class="entry.kind === 'note' ? 'rounded-2xl bg-surface-elevation-1 p-4' : ''"
          :data-testid="`lead-history-${entryTestId(entry.kind)}`"
        >
          <p class="mb-1 txt-label-s text-subtlest">
            <span data-testid="lead-history-actor">{{ entry.actorName || t('LeadDetail.history.systemActor') }}</span>
            <span> · </span>
            <time :datetime="entry.createdAt" class="whitespace-nowrap">{{ d(entry.createdAt, 'dateTimeShort') }}</time>
          </p>
          <p v-if="entry.kind === 'note'" class="txt-body-m whitespace-pre-line" data-testid="lead-note-body">{{ entry.body }}</p>
          <p v-else class="flex items-center gap-2 txt-body-m" data-testid="lead-history-status-badges">
            {{ t('LeadDetail.history.statusChanged') }}
            <ChipBadge v-if="entry.fromStatus" :color="badgeColorByStatus[entry.fromStatus as LeadStatusEnum]" rounded>
              {{ t(leadStatusLabelKey(entry.fromStatus)) }}
            </ChipBadge>
            <ArrowRightIcon class="lucide-icon-s text-icon-subtlest" />
            <ChipBadge v-if="entry.toStatus" :color="badgeColorByStatus[entry.toStatus as LeadStatusEnum]" rounded>
              {{ t(leadStatusLabelKey(entry.toStatus)) }}
            </ChipBadge>
          </p>
        </li>
      </ul>
      <ShowMoreToggle
        v-if="hiddenCount > 0"
        v-model:expanded="expanded"
        class="mt-4"
        :more-label="t('LeadDetail.history.showMore', { count: hiddenCount })"
        :less-label="t('LeadDetail.history.showLess')"
        data-testid="lead-history-toggle"
      />
    </template>

    <LeadNoteDialog ref="noteDialog" :lead-id="leadId" />
  </section>
</template>

<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { ArrowRightIcon, PencilIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { type LeadStatusEnum } from '@/gql/reviews'
import ShowMoreToggle from '@/ui/ShowMoreToggle.vue'

import LeadNoteDialog from './LeadNoteDialog.vue'
import { badgeColorByStatus, leadStatusLabelKey } from './leadStatus'
import LeadStatusSelect from './LeadStatusSelect.vue'
import type { LeadHistoryEntry } from './types'

const VISIBLE_ENTRY_LIMIT = 3

const props = defineProps<{
  leadId: string
  entries: LeadHistoryEntry[]
  status: LeadStatusEnum
  statusLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:status', value: LeadStatusEnum): void
}>()

const { t, d } = useI18n()

const noteDialog = ref<InstanceType<typeof LeadNoteDialog> | null>(null)
const expanded = ref(false)

const entryTestId = (kind: LeadHistoryEntry['kind']) => (kind === 'note' ? 'note' : 'status')

const visibleEntries = computed(() => (expanded.value ? props.entries : props.entries.slice(0, VISIBLE_ENTRY_LIMIT)))
const hiddenCount = computed(() => Math.max(0, props.entries.length - VISIBLE_ENTRY_LIMIT))
</script>
